import { Form, Badge, Spinner } from 'react-bootstrap';
import { useMaterialSpecs } from '../../features/material-specs/hooks/useMaterialSpecs';

/**
 * Pick a material from the catalogue instead of retyping it.
 *
 * Narrowed to the phase being filled in, so choosing an inner sheath does not
 * mean scrolling past every insulation colour the factory has ever run. Picking
 * fills material, type, density and colour; the geometry stays the engineer's.
 *
 * Selecting nothing is allowed on purpose — a compound that is genuinely new
 * has to be enterable before anyone has catalogued it.
 */
const MaterialPicker = ({ usedIn, value, onPick, label = 'Quick select' }) => {
  const { data: specs = [], isLoading } = useMaterialSpecs({ usedIn });

  const labelOf = s =>
    [s.material, s.type, s.color].filter(Boolean).join(' ') +
    (s.density ? ` · ρ ${s.density}` : ' · no density');

  return (
    <div className="mb-3">
      <Form.Label className="fs-9 fw-semibold text-700 mb-1">
        {label}
        {isLoading && <Spinner animation="border" size="sm" className="ms-2" />}
      </Form.Label>
      <Form.Select
        size="sm"
        value={value || ''}
        onChange={e => onPick(specs.find(s => s._id === e.target.value) || null)}
      >
        <option value="">— type it in by hand —</option>
        {specs.map(s => (
          <option key={s._id} value={s._id}>
            {labelOf(s)}
            {s.standardsCount ? ` (${s.standardsCount})` : ''}
          </option>
        ))}
      </Form.Select>
      {!isLoading && !specs.length && (
        <Form.Text className="text-warning fs-10">
          Nothing catalogued for this phase yet — fill the fields in by hand, or add it in the
          material catalogue.
        </Form.Text>
      )}
      {!!specs.length && (
        <Form.Text className="text-700 fs-10">
          {specs.length} material(s){' '}
          {specs.some(s => s.densityConflict) && (
            <Badge bg="warning" className="fs-10 ms-1">
              some have a density conflict
            </Badge>
          )}
        </Form.Text>
      )}
    </div>
  );
};

export default MaterialPicker;
